import TrebbleCard from "../../../../common/TrebbleCard";
import TrebbleButton from "../../../../common/TrebbleButton";
import TrebbleSkeleton from "../../../../common/TrebbleSkeleton";
import { Template } from "../../VideoEditor";
import { useState, useEffect, useRef } from "react";
import { ThemeTemplate } from "./ThemeTemplate";
import { ThemeEditForm } from "./ThemeEditForm";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { ThemeSystemConfig } from "../../../remotion/Components/Theme/Theme";
import TrebbleClientAPIHelper from "../../../../../models/helper/TrebbleClientAPI";
import ti18n from 'i18n!nls/ClipCreatori18n';
import useDefaultTrebbleAntDTokenConfig from "../../../../helper/useDefaultTrebbleAntDTokenConfig";

declare global {
    interface Window {
        alertErrorMessage: (error: any) => void;
        getI18n: (i18nData: any, key: string, options?: any) => string;
    }
}

const defaultThemeConfig: ThemeSystemConfig = {
    brandConfig: {
        primaryColor: "#000000",
        secondaryColor: "#ffffff",
        accentColor: "#cccccc",
        titleFont: "Inter",
        bodyFont: "Inter",
        logoURL: "",
        titleFontOutline: "none" as const,
        bodyFontOutline: "none" as const,
        borderRadius: 0.1
    },
    backgroundConfig: {
        format: "color" as const,
        color: "#ffffff",
        imageURL: ""
    },
    frameConfig: {
        padding: true,
        borderRadius: 30,
        border: false,
        borderStyle: "no_border"
    },
    savedCaptionConfig: undefined
};

// Update Theme interface to better handle saved vs unsaved states
export interface Theme {
    id: string;
    name: string;
    type: 'user' | 'trebble';
    category?: string;
    themeConfig: ThemeSystemConfig;
    isSaved?: boolean;
}

// Add interface for the API response theme format
interface ApiTheme {
    id: string;
    name: string;
    category?: string;
    themeConfig: ThemeSystemConfig;
}

// Replace simulated API calls with actual API calls
const fetchUserThemes = async (workspaceId: string | null | undefined) => {
    const api = TrebbleClientAPIHelper.getInstance();
    return api.getThemes({ type: 'user' }, workspaceId);
};

const fetchTrebbleThemes = async (workspaceId: string | null | undefined) => {
    const api = TrebbleClientAPIHelper.getInstance();
    return api.getThemes({ type: 'trebble' }, workspaceId);
};

const saveTheme = async (theme: Theme, workspaceId: string | null | undefined) => {
    const api = TrebbleClientAPIHelper.getInstance();
    if (theme.isSaved) {
        return api.updateTheme(theme.id, theme, workspaceId);
    } else {
        return api.createTheme(theme, workspaceId);
    }
};

interface ThemeConfigProps {
    template: Template;
    onTemplateChange: (template: Template) => void;
    onEditingChange: (isEditing: boolean) => void;
    workspaceId: string | null | undefined;
}

export const ThemeConfig = ({ template, onTemplateChange, onEditingChange, workspaceId }: ThemeConfigProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingTheme, setEditingTheme] = useState<Theme | null>(null);
    const [userThemes, setUserThemes] = useState<Theme[]>([]);
    const [trebbleThemes, setTrebbleThemes] = useState<Theme[]>([]);
    const [trebbleThemesByCategory, setTrebbleThemesByCategory] = useState<{ [key: string]: Theme[] }>({});
    const [isThemePickerOpen, setIsThemePickerOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [originalThemeBeforeEdit, setOriginalThemeBeforeEdit] = useState<Theme | null>(null);
    const token = useDefaultTrebbleAntDTokenConfig();

    // Refs to hold the latest values for the cleanup function
    const isEditingRef = useRef(isEditing);
    const originalThemeRef = useRef(originalThemeBeforeEdit);
    const templateRef = useRef(template);
    const onTemplateChangeRef = useRef(onTemplateChange);

    // Effect to keep refs updated with the latest values
    useEffect(() => {
        isEditingRef.current = isEditing;
        originalThemeRef.current = originalThemeBeforeEdit;
        templateRef.current = template;
        onTemplateChangeRef.current = onTemplateChange;
    }, [isEditing, originalThemeBeforeEdit, template, onTemplateChange]);

    // Get the currently selected theme
    const selectedThemeDetails = [...userThemes, ...trebbleThemes].find(t =>
        t.id === template.savedThemeConfig?.id
    ) || userThemes[0] || trebbleThemes[0];

    // Load themes on component mount
    useEffect(() => {
        const loadThemes = async () => {
            setIsLoading(true);
            try {
                const [userThemesData, trebbleThemesData] = await Promise.all([
                    fetchUserThemes(workspaceId),
                    fetchTrebbleThemes(workspaceId)
                ]);

                // Organize Trebble themes by category
                const categorizedThemes = trebbleThemesData.reduce((acc: { [key: string]: Theme[] }, theme: ApiTheme) => {
                    const category = theme.category || window.getI18n(ti18n, "OTHER_CATEGORY");
                    if (!acc[category]) {
                        acc[category] = [];
                    }
                    const themeObj: Theme = {
                        id: theme.id,
                        name: theme.name,
                        type: 'trebble',
                        category: theme.category,
                        isSaved: true,
                        themeConfig: theme.themeConfig
                    };
                    acc[category].push(themeObj);
                    return acc;
                }, {});

                setTrebbleThemesByCategory(categorizedThemes);

                const userThemesList = userThemesData.map((theme: ApiTheme) => ({
                    id: theme.id,
                    name: theme.name,
                    type: 'user',
                    category: theme.category,
                    isSaved: true,
                    themeConfig: theme.themeConfig
                }));

                const trebbleThemesList = trebbleThemesData.map((theme: ApiTheme) => ({
                    id: theme.id,
                    name: theme.name,
                    type: 'trebble',
                    category: theme.category,
                    isSaved: true,
                    themeConfig: theme.themeConfig
                }));

                setUserThemes(userThemesList);
                setTrebbleThemes(trebbleThemesList);
            } catch (error) {
                console.error("Error loading themes:", error);
                if (typeof window.alertErrorMessage === 'function') {
                    window.alertErrorMessage(error);
                }
            } finally {
                setIsLoading(false);
            }
        };
        loadThemes();
    }, [workspaceId]);

    const handleThemeSelect = (theme: Theme) => {
        if (theme.id === template?.savedThemeConfig?.id) return;
        onTemplateChange({
            ...template,
            savedThemeConfig: theme
        });
    };

    const handleEdit = () => {
        if (!selectedThemeDetails || selectedThemeDetails.type === 'trebble') return;
        // Save scroll position before entering edit mode
        if (scrollContainerRef.current) {
            setScrollPosition(scrollContainerRef.current.scrollTop);
        }
        // Store the original theme to revert to on cancel
        setOriginalThemeBeforeEdit(selectedThemeDetails);
        setEditingTheme(selectedThemeDetails);
        setIsEditing(true);
        onEditingChange(true);
    };

    const handleDuplicate = () => {
        if (!selectedThemeDetails) return;

        const newId =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const duplicatedTheme: Theme = {
            id: newId,
            name: `${selectedThemeDetails.name} ${window.getI18n(ti18n, "THEME_NAME_COPY_SUFFIX")}`,
            type: 'user' as const,
            category: selectedThemeDetails.category,
            isSaved: false,
            themeConfig:
              typeof structuredClone === 'function'
                ? structuredClone(selectedThemeDetails.themeConfig)
                : JSON.parse(JSON.stringify(selectedThemeDetails.themeConfig))
        };

        // Store the original theme being duplicated to revert to on cancel
        setOriginalThemeBeforeEdit(selectedThemeDetails);
        setEditingTheme(duplicatedTheme);
        setIsEditing(true);
        onEditingChange(true);
        // Update preview immediately to show the unsaved duplicated theme
        onTemplateChange({
            ...template,
            savedThemeConfig: duplicatedTheme
        });
    };

    const handleSave = async () => {
        if (!editingTheme) return;
        
        try {
            const themeToSave = {
                ...editingTheme,
                themeConfig: {
                    brandConfig: editingTheme.themeConfig.brandConfig,
                    backgroundConfig: editingTheme.themeConfig.backgroundConfig,
                    frameConfig: editingTheme.themeConfig.frameConfig,
                    savedCaptionConfig: editingTheme.themeConfig.savedCaptionConfig
                }
            };

            const savedTheme = await saveTheme(themeToSave, workspaceId) as ApiTheme;
            const updatedTheme: Theme = {
                id: savedTheme.id,
                name: savedTheme.name ?? editingTheme.name,
                type: 'user', // Persist as user theme after save
                category: savedTheme.category ?? editingTheme.category,
                themeConfig: savedTheme.themeConfig ?? editingTheme.themeConfig,
                isSaved: true
            };
            
            // Update local state based on whether it was an existing user theme or a new/duplicated one
            if (editingTheme.isSaved === false || !userThemes.find(t => t.id === editingTheme.id)) {
                // Add new theme if it was previously unsaved (new or duplicated)
                setUserThemes(prev => [...prev, updatedTheme]);
            } else {
                // Update existing theme
                setUserThemes(prev => prev.map(theme => 
                    theme.id === updatedTheme.id ? updatedTheme : theme
                ));
            }
            
            // Set the newly saved theme as the current selection
            onTemplateChange({
                ...template,
                savedThemeConfig: updatedTheme
            });
            setIsEditing(false);
            setEditingTheme(null);
            setOriginalThemeBeforeEdit(null); // Clear original theme state
            onEditingChange(false);
        } catch (error) {
            console.error("Error saving theme:", error);
            if (typeof window.alertErrorMessage === 'function') {
                window.alertErrorMessage(error);
            }
        }
    };

    // Effect to handle reverting changes ONLY if the component unmounts while editing
    useEffect(() => {
        // This effect runs only on mount because of the empty dependency array
        return () => {
            // Cleanup function runs only on unmount
            // Access the latest values via refs
            if (isEditingRef.current && originalThemeRef.current) {
                console.log("ThemeConfig unmounting while editing, reverting theme...");
                onTemplateChangeRef.current({
                    ...templateRef.current, // Use template value from ref
                    savedThemeConfig: originalThemeRef.current
                });
                onEditingChange(false); // Signal editing end on unmount revert
            }
        };
    }, []); // <-- Empty dependency array ensures cleanup runs only on unmount

    const handleCreateNew = () => {
        const newId = Date.now().toString();
        const newTheme: Theme = {
            id: newId,
            name: window.getI18n(ti18n, "NEW_THEME_DEFAULT_NAME"),
            type: 'user' as const,
            isSaved: false,
            themeConfig: {
                brandConfig: {
                    primaryColor: "#000000",
                    secondaryColor: "#ffffff",
                    accentColor: "#cccccc",
                    titleFont: "Inter",
                    bodyFont: "Inter",
                    logoURL: "",
                    titleFontOutline: "none" as const,
                    bodyFontOutline: "none" as const,
                    borderRadius: 0.1
                },
                backgroundConfig: {
                    format: "color" as const,
                    color: "#ffffff",
                    imageURL: ""
                },
                frameConfig: {
                    padding: true,
                    borderRadius: 30,
                    border: false,
                    borderStyle: "no_border"
                },
                savedCaptionConfig: {
                    id: "default_caption",
                    name: window.getI18n(ti18n, "THEME_DEFAULT_CAPTION_NAME"),
                    type: "user" as const,
                    captionConfig: {
                        milisecondGrouping: 1000,
                        font: "Inter",
                        useDesignSystemColor: true,
                        mainColor: "#000000",
                        highlightColor: "#ffffff",
                        fontSizeRatio: "medium",
                        outline: "none",
                        background: "none",
                        textAnimation: "none",
                        borderRadius: 0.1,
                        captionCase: "standard",
                        hidePunctuation: false,
                        position: "bottom center",
                        maxLines: "two_lines",
                        showOneWordOnly: false
                    }
                }
            }
        };

        // Store the currently selected theme to revert to on cancel
        setOriginalThemeBeforeEdit(selectedThemeDetails || null);
        setEditingTheme(newTheme);
        setIsEditing(true);
        onEditingChange(true);
        // Update preview immediately to show the new unsaved theme
        onTemplateChange({
            ...template,
            savedThemeConfig: newTheme
        });
    };

    if (isEditing && editingTheme) {
        return (
            <div className="flex flex-col h-full">
                {/* Header */}
                {!isThemePickerOpen && (
                    <div className="flex items-center">
                        <TrebbleButton 
                            type="text" 
                            icon={<ArrowLeftOutlined />}
                            onClick={() => {
                                // Revert to the original theme when cancelling
                                if (originalThemeBeforeEdit) {
                                    onTemplateChange({
                                        ...template,
                                        savedThemeConfig: originalThemeBeforeEdit
                                    });
                                }
                                setIsEditing(false);
                                setEditingTheme(null);
                                setOriginalThemeBeforeEdit(null); // Clear original theme state
                                // Restore scroll position after a brief delay to ensure DOM is updated
                                setTimeout(() => {
                                    if (scrollContainerRef.current) {
                                        scrollContainerRef.current.scrollTop = scrollPosition;
                                    }
                                }, 0);
                                onEditingChange(false);
                            }}
                        >
                            {window.getI18n(ti18n, "BACK_BUTTON")}
                        </TrebbleButton>
                    </div>
                )}

                {/* Edit Form */}
                <div className="flex-grow overflow-auto">
                    <ThemeEditForm 
                        theme={editingTheme}
                        onThemeChange={(updatedTheme) => {
                            setEditingTheme(updatedTheme);
                            onTemplateChange({
                                ...template,
                                savedThemeConfig: updatedTheme
                            });
                        }}
                        onPickerVisibilityChange={setIsThemePickerOpen}
                        workspaceId={workspaceId}
                    />
                </div>

                {/* Fixed Bottom Bar */}
                <div className="sticky bottom-0 bg-white border-t p-4 mt-4 z-20 dark:bg-neutral-800 dark:border-neutral-600">
                    <TrebbleButton type="primary" block onClick={handleSave}>
                        {window.getI18n(ti18n, "SAVE_THEME_BUTTON")}
                    </TrebbleButton>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex flex-col h-full">
                {/* Create New Theme Button Skeleton */}
                <div className="sticky top-0 z-20 bg-white border-b p-4 dark:bg-neutral-800 dark:border-neutral-600">
                    <TrebbleSkeleton.Button active block style={{ height: 35, borderRadius: 0 }} />
                </div>
                {/* Content Skeleton */}
                <div className="flex-grow overflow-auto">
                    {/* Category Header Skeleton Line */}
                    <div className="sticky top-0 z-10 bg-white dark:bg-neutral-800 dark:text-white mt-4 border-x border-t border-neutral-100 dark:border-neutral-600">
                        <TrebbleSkeleton.Input active style={{ width: 120, height: 32, margin: 16, background: token.token.colorSplit }} />
                    </div>
                    <TrebbleCard size="small">
                        <div className="grid mb-8">
                            {[...Array(2)].map((_, idx) => (
                                <TrebbleSkeleton
                                    key={idx}
                                    active
                                    style={{ width: 160, height: 80, borderRadius: 0 }}
                                    paragraph={true}
                                    title={{ width: '60%' }}
                                />
                            ))}
                        </div>
                    </TrebbleCard>
                </div>
                {/* Bottom Bar Skeleton */}
                <div className="sticky bottom-0 bg-white border-t p-4 mt-4 z-20 dark:bg-neutral-800 dark:border-neutral-600">
                    <div className="flex gap-2">
                        <TrebbleSkeleton.Button active block style={{ height: 32, borderRadius: 0, width: '100%', background: token.token.colorSplit }} />
                        <TrebbleSkeleton.Button active block style={{ height: 32, borderRadius: 0, width: '100%', background: token.token.colorSplit }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Create New Theme Button */}
            <div className="sticky top-0 z-20 bg-white border-b p-4 dark:bg-neutral-800 dark:border-neutral-600">
                <TrebbleButton 
                    type="primary" 
                    block 
                    onClick={handleCreateNew}
                >
                    {window.getI18n(ti18n, "CREATE_NEW_THEME_BUTTON")}
                </TrebbleButton>
            </div>

            {/* Scrollable Content */}
            <div ref={scrollContainerRef} className="flex-grow overflow-auto">
                {/* Your Themes */}
                {userThemes?.filter(Boolean).length > 0 && (
                    <>
                        <div className="sticky top-0 z-10 bg-white dark:bg-neutral-800 dark:text-white mt-4 border-x border-t border-neutral-100 dark:border-neutral-600" >
                            <h2 className="py-3 px-3 font-bold">{window.getI18n(ti18n, "YOUR_THEMES_SECTION_TITLE").toUpperCase()}</h2>
                        </div>
                        <TrebbleCard size="small">
                            <div className="grid grid-cols-2 gap-2 p-2 mb-8">
                                {userThemes?.filter(Boolean).map(theme => (
                                    <ThemeTemplate
                                        key={theme.id}
                                        name={theme.name}
                                        compact={true}
                                        themeConfig={theme.themeConfig ?? defaultThemeConfig}
                                        onClick={() => handleThemeSelect(theme)}
                                        selected={theme.id === template.savedThemeConfig?.id}
                                    />
                                ))}
                            </div>
                        </TrebbleCard>
                    </>
                )}

                {/* Trebble Themes by Category */}
                {Object.entries(trebbleThemesByCategory).map(([category, themes]) => (
                    <div key={category}>
                        <div className="sticky top-0 z-10 bg-white dark:bg-neutral-800 dark:text-white mt-4 border-x border-t border-neutral-100 dark:border-neutral-600">
                            <h2 className="py-3 px-3 font-bold">{category.toUpperCase()}</h2>
                        </div>
                        <TrebbleCard size="small">
                            <div className="grid grid-cols-2 gap-2 p-2 mb-8">
                                {themes.map(theme => (
                                    <ThemeTemplate
                                        key={theme.id}
                                        name={theme.name}
                                        compact={true}
                                        themeConfig={theme.themeConfig}
                                        onClick={() => handleThemeSelect(theme)}
                                        selected={theme.id === template.savedThemeConfig?.id}
                                    />
                                ))}
                            </div>
                        </TrebbleCard>
                    </div>
                ))}
            </div>

            {/* Fixed Bottom Bar */}
            <div className="sticky bottom-0 bg-white border-t p-4 mt-4 z-20 dark:bg-neutral-800 dark:border-neutral-600">
                <div className="flex gap-2">
                    <TrebbleButton 
                        block 
                        disabled={!selectedThemeDetails || selectedThemeDetails.type === 'trebble'}
                        onClick={handleEdit}
                    >
                        {window.getI18n(ti18n, "EDIT_BUTTON")}
                    </TrebbleButton>
                    <TrebbleButton 
                        block 
                        type="primary" 
                        disabled={!selectedThemeDetails}
                        onClick={handleDuplicate}
                    >
                        {window.getI18n(ti18n, "DUPLICATE_BUTTON")}
                    </TrebbleButton>
                </div>
            </div>
        </div>
    );
};
